/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useQuery, gql, TypedDocumentNode } from "@apollo/client";
import useAmplitude from "@/hooks/useAmplitude";
import { PhotoboothPerson } from "@/pages/photobooth/queries";
import {
  PHOTOBOOTH_ANALYTICS_EVENTS,
  getPhotoboothOpenedAnalyticsData,
  type PhotoboothAnalyticsSource
} from "@/pages/photobooth/_utils/amplitude";

type PhotoboothMode = "upload" | "camera";

export type OpenPhotoboothOptions = {
  mode?: PhotoboothMode;
  source: PhotoboothAnalyticsSource;
};

interface PhotoboothModalState {
  isOpen: boolean;
  pgaId: string | null;
  initialMode: PhotoboothMode;
  analyticsSource: PhotoboothAnalyticsSource | null;
}

interface PhotoboothContextValue {
  modalState: PhotoboothModalState;
  openPhotoboothByPgaId: (pgaId: string, options: OpenPhotoboothOptions) => void;
  closePhotobooth: () => void;
}

const PhotoboothContext = createContext<PhotoboothContextValue | null>(null);

type PhotoboothPersonByPgaIdResponse = {
  person: PhotoboothPerson | null;
};

type PhotoboothPersonByPgaIdVariables = {
  pgaId: string;
};

export const PHOTOBOOTH_PERSON_BY_PGAID_QUERY: TypedDocumentNode<
  PhotoboothPersonByPgaIdResponse,
  PhotoboothPersonByPgaIdVariables
> = gql`
  query PhotoboothPersonByPgaId($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      firstName
      lastName
      profilePhoto
      addresses {
        city
        state
        addressType
      }
    }
  }
`;

interface PhotoboothProviderProps {
  children: ReactNode;
}

export function PhotoboothProvider({ children }: PhotoboothProviderProps) {
  const { trackEvent } = useAmplitude();
  const [modalState, setModalState] = useState<PhotoboothModalState>({
    isOpen: false,
    pgaId: null,
    initialMode: "upload",
    analyticsSource: null
  });

  const openPhotoboothByPgaId = useCallback(
    (pgaId: string, options: OpenPhotoboothOptions) => {
      const mode = options.mode ?? "upload";
      trackEvent(
        PHOTOBOOTH_ANALYTICS_EVENTS.opened,
        getPhotoboothOpenedAnalyticsData({
          pgaId,
          source: options.source,
          mode
        })
      );
      setModalState({
        isOpen: true,
        pgaId,
        initialMode: mode,
        analyticsSource: options.source
      });
    },
    [trackEvent]
  );

  const closePhotobooth = useCallback(() => {
    setModalState({
      isOpen: false,
      pgaId: null,
      initialMode: "upload",
      analyticsSource: null
    });
  }, []);

  return (
    <PhotoboothContext.Provider value={{ modalState, openPhotoboothByPgaId, closePhotobooth }}>
      {children}
    </PhotoboothContext.Provider>
  );
}

export function usePhotoboothOptional() {
  return useContext(PhotoboothContext);
}

export function usePhotoboothPerson(): { person: PhotoboothPerson | null; loading: boolean } {
  const context = useContext(PhotoboothContext);
  const pgaId = context?.modalState.pgaId;
  const isOpen = context?.modalState.isOpen;

  const { data, loading } = useQuery(PHOTOBOOTH_PERSON_BY_PGAID_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !isOpen
  });

  return {
    person: data?.person || null,
    loading
  };
}
